import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { getPatientExternalOrderRendersPatientsPatientIdExternalOrderRendersGet as getExternalOrderRenders } from '../../services/api/sdk.gen';
import type { PatientDocumentResponse } from '../../services/api/types.gen';

interface ExternalOrderRendersProps {
  patientId: string;
  /** Narrow to one episode's renders; omit for the whole admission. */
  episodeId?: string | null;
}

/**
 * The previous EMR's order renders, listed under the Orders filter (EMR-1860).
 * An order in the previous EMR never becomes an order in Roger, so the Orders
 * tab of an imported chart was empty while the orders sat in Documents. Each
 * row opens the document. Renders imported before they carried an episode
 * appear only in the admission-wide list, which is why the section says so.
 */
export function ExternalOrderRenders({
  patientId,
  episodeId,
}: ExternalOrderRendersProps) {
  const [renders, setRenders] = useState<PatientDocumentResponse[] | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;
    setRenders(null);
    void (async () => {
      const resp = await getExternalOrderRenders({
        path: { patient_id: patientId },
        query: episodeId ? { episode_id: episodeId } : undefined,
      });
      if (cancelled) return;
      if (resp.error || !resp.data) {
        console.error('Failed to load external order renders', resp.error);
        setRenders([]);
        return;
      }
      setRenders(resp.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId, episodeId]);

  if (!renders || renders.length === 0) return null;

  return (
    <section
      className='mt-3 rounded-md border border-border/60 bg-background/40 p-3'
      data-testid='external-order-renders'
    >
      <h3 className='text-sm font-medium'>From the previous EMR</h3>
      <p className='text-xs text-muted-foreground'>
        Orders the previous EMR held, as it printed them. They are read-only.
        {episodeId
          ? ' Orders it filed under this episode.'
          : ' Every order on the admission.'}
      </p>
      <ul className='mt-2 space-y-1'>
        {renders.map(doc => (
          <li key={doc.id} className='flex items-center gap-2 text-sm'>
            <FileText className='h-4 w-4 shrink-0 text-muted-foreground' />
            <Link
              to={`/patients/${patientId}/documents/${doc.id}`}
              className='underline-offset-2 hover:underline'
            >
              {doc.file_name}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
